import type {
  Finding,
  PartId,
  Probe,
  ProbeContext,
  ProbeOutcome,
  ProbeResult,
  SignalValue,
} from './types';
import { getProbes } from './registry';

export interface RunnerEvents {
  /** Fired as each probe settles, so the UI can fill in progressively. */
  onOutcome(outcome: ProbeOutcome): void;
  /** Fired once when all probes have settled. */
  onComplete(outcomes: ProbeOutcome[]): void;
}

export interface RunnerOptions {
  globalDeadlineMs?: number;
  gestureGranted?: boolean;
}

/**
 * Runs every registered probe.
 *
 * Probes execute in WAVES. A probe declaring `after: [ids]` runs in a later
 * wave, once those probes have settled. This exists because some probes reason
 * across siblings — the coherence checks read the User-Agent, the WebGL renderer
 * and the client hints — and a single concurrent wave would race them.
 *
 * Invariants:
 *  - a probe that throws becomes a ProbeOutcome with state 'error'; it never
 *    rejects the runner and never aborts sibling probes
 *  - a probe that hangs is abandoned at budgetMs and reported 'timeout'
 *  - the global deadline caps the total across all waves
 */
export async function runProbes(
  events: RunnerEvents,
  opts: RunnerOptions = {},
): Promise<ProbeOutcome[]> {
  const probes = getProbes();
  const globalDeadlineMs = opts.globalDeadlineMs ?? 30_000;
  const gestureGranted = opts.gestureGranted ?? false;
  const deadline = Date.now() + globalDeadlineMs;

  // Raise the Resource Timing buffer before any probe runs. The default holds
  // 250 entries, and with ~45 probes each issuing several requests the buffer
  // overflows mid-scan — which silently starves later probes (the DNS probe
  // reads Resource-Timing entries) of their own measurements. This is exactly
  // the kind of cross-probe interference the wave system is meant to prevent.
  try {
    if (typeof performance !== 'undefined') {
      const resTiming = performance as Performance & {
        setResourceTimingBufferSize?: (n: number) => void;
        resourcetimingbufferfull?: (cb: () => void) => void;
      };
      resTiming.setResourceTimingBufferSize?.(2000);
    }
  } catch {
    // Older engines: the DNS probe degrades to reporting 0 samples.
  }

  const settled: ProbeOutcome[] = [];
  const settledIds = new Set<string>();

  // Live, mutable view of what has been collected so far. Probes that reason
  // across siblings read through this during their wave.
  const signalStore: Record<string, Record<string, SignalValue>> = {};

  const finish = (outcome: ProbeOutcome) => {
    settled.push(outcome);
    settledIds.add(outcome.probe.id);
    signalStore[outcome.probe.id] = outcome.signals;
    events.onOutcome(outcome);
  };

  const makeCtx = (probe: Probe, started: number): ProbeContext => {
    const budget = Math.max(1, Math.min(probe.budgetMs, deadline - started));
    return {
      deadline: Math.min(deadline, started + budget),
      get: () => undefined,
      readSignal: <T,>(probeId: string, signal: string) =>
        signalStore[probeId]?.[signal]?.value as T | undefined,
      gestureGranted,
    };
  };

  // --- gesture-gated probes run first, and only when a gesture was granted ---
  // No probe currently sets requiresGesture (portScan runs on every scan and
  // reports a blocked Local Network Access prompt honestly). The mechanism is
  // retained because it is the right behaviour for a probe that genuinely
  // cannot run without one; runner.test.ts covers it.
  const gated = probes.filter((p) => p.requiresGesture);
  if (gated.length && gestureGranted) {
    await Promise.all(
      gated.map((probe) => {
        const started = Date.now();
        return runOne(probe, started, makeCtx(probe, started)).then(finish);
      }),
    );
  } else {
    for (const probe of gated) {
      finish({
        probe,
        state: 'skipped',
        signals: {},
        findings: [],
        durationMs: 0,
        error: 'requires user gesture',
      });
    }
  }

  // --- remaining probes, grouped into dependency waves --------------------
  const rest = probes.filter((p) => !p.requiresGesture);
  const pending = new Set(rest.map((p) => p.id));

  let guard = 0;
  while (pending.size > 0) {
    if (++guard > rest.length + 1) break; // defensive: never spin

    // Ready = every declared dependency has settled. An unknown dependency id
    // counts as satisfied so a typo degrades to "partial data", not a deadlock.
    const ready = rest.filter(
      (p) => pending.has(p.id) && (p.after ?? []).every((dep) => settledIds.has(dep) || !pending.has(dep)),
    );
    if (ready.length === 0) {
      // Cycle: run the remainder rather than stalling. They see a partial
      // signal store, which the probes document and handle.
      const byId = new Map(rest.map((p) => [p.id, p]));
      for (const id of pending) {
        const probe = byId.get(id);
        if (!probe) continue;
        finish({
          probe,
          state: 'skipped',
          signals: {},
          findings: [],
          durationMs: 0,
          error: 'unresolvable probe dependencies',
        });
      }
      break;
    }

    for (const p of ready) pending.delete(p.id);

    await Promise.all(
      ready.map((probe) => {
        const started = Date.now();
        return runOne(probe, started, makeCtx(probe, started)).then(finish);
      }),
    );
  }

  events.onComplete(settled);
  return settled;
}

async function runOne(
  probe: Probe,
  started: number,
  ctx: ProbeContext,
): Promise<ProbeOutcome> {
  const budget = Math.max(1, Math.min(probe.budgetMs, Math.max(1, ctx.deadline - started)));

  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const result = (await Promise.race([
      probe.run(ctx),
      new Promise<never>((_, reject) => {
        timer = setTimeout(
          () => reject(Object.assign(new Error(`timeout after ${budget}ms`), { __timeout: true })),
          budget,
        );
      }),
    ])) as ProbeResult;

    return {
      probe,
      state: 'done',
      signals: result.signals ?? {},
      findings: result.findings ?? [],
      durationMs: Date.now() - started,
      partInputs: result.parts,
    };
  } catch (err) {
    const isTimeout = !!(err as { __timeout?: boolean })?.__timeout;
    return {
      probe,
      state: isTimeout ? 'timeout' : 'error',
      signals: {},
      findings: [],
      durationMs: Date.now() - started,
      error: isTimeout ? `timed out after ${budget}ms` : String((err as Error)?.message ?? err),
    };
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** Convenience for probes: build an ok SignalValue. */
export function ok<T>(value: T, origin: SignalValue['origin'] = 'client', durationMs = 0): SignalValue<T> {
  return { value, status: 'ok', origin, durationMs };
}

/** Convenience for probes: an unavailable/unsupported signal. */
export function na<T = null>(
  status: SignalValue['status'] = 'unavailable',
  origin: SignalValue['origin'] = 'client',
  error?: string,
): SignalValue<T | null> {
  const out: SignalValue<T | null> = { value: null, status, origin, durationMs: 0 };
  if (error) out.error = error;
  return out;
}

export type { Finding, PartId };
